import { Card, ListLayout } from '@ingrid/luterkort-react';
import { ShippingLogo } from '@ingrid/react-shipping-logo';

import { LocalizedDate } from '~src/i18n/localized-date';
import { useTranslation } from '~src/i18n/use-translation';

import { formatAddonTitle } from '../create/carrier-product-addons';
import { ShipmentDetails } from '../types';

interface Props {
  shipment: Pick<ShipmentDetails, 'shipping_method' | 'shipping_date' | 'delivery_time' | 'addons' | 'location_ref'>;
}

export function CarrierProduct({ shipment }: Props) {
  const { t } = useTranslation();

  return (
    <Card.Wrapper>
      <Card.Header>{t('common.carrierProduct')}</Card.Header>
      <Card.Content>
        <ListLayout.Column>
          <ListLayout.Row>
            <ShippingLogo method={shipment.shipping_method.id} width="40" />
            <ListLayout.Stack>
              <ListLayout.PrimaryText>{shipment.shipping_method.carrier}</ListLayout.PrimaryText>
              <ListLayout.SecondaryText>{shipment.shipping_method.name}</ListLayout.SecondaryText>
            </ListLayout.Stack>
          </ListLayout.Row>
          <ListLayout.Row>
            <ListLayout.Stack>
              <ListLayout.SecondaryText>{t('common.shippingDate')}</ListLayout.SecondaryText>
              <ListLayout.PrimaryText>
                <LocalizedDate
                  options={{ weekday: 'long', month: 'long', year: undefined }}
                  dateTime={shipment.shipping_date!}
                />
              </ListLayout.PrimaryText>
            </ListLayout.Stack>
          </ListLayout.Row>
          {shipment.delivery_time && (
            <ListLayout.Row>
              <ListLayout.Stack>
                <ListLayout.SecondaryText>{t('common.deliveryTime')}</ListLayout.SecondaryText>
                <ListLayout.PrimaryText>
                  <LocalizedDate
                    options={{ weekday: 'long', month: 'long', year: undefined }}
                    // eslint-disable-next-line @typescript-eslint/non-nullable-type-assertion-style
                    dateTime={shipment.delivery_time?.start as string}
                    inline
                    time
                  />{' '}
                  -{' '}
                  <LocalizedDate
                    options={{ weekday: 'long', month: 'long', year: undefined }}
                    // eslint-disable-next-line @typescript-eslint/non-nullable-type-assertion-style
                    dateTime={shipment.delivery_time?.end as string}
                    inline
                    time
                  />
                </ListLayout.PrimaryText>
              </ListLayout.Stack>
            </ListLayout.Row>
          )}
        </ListLayout.Column>
      </Card.Content>
      {shipment.addons && shipment.addons.length > 0 && (
        <Card.Content>
          <ListLayout.Stack>
            <ListLayout.SecondaryText>{t('common.addons')}</ListLayout.SecondaryText>
            <ListLayout.SubRow gap="lg">
              {shipment.addons.map(addon => (
                <ListLayout.PrimaryText key={addon.code}>{formatAddonTitle(addon)}</ListLayout.PrimaryText>
              ))}
            </ListLayout.SubRow>
          </ListLayout.Stack>
        </Card.Content>
      )}
      {shipment.location_ref && (
        <Card.Content>
          <ListLayout.Stack>
            <ListLayout.SecondaryText>{t('common.pickupPoint')}</ListLayout.SecondaryText>
            <ListLayout.PrimaryText>{shipment.location_ref}</ListLayout.PrimaryText>
          </ListLayout.Stack>
        </Card.Content>
      )}
    </Card.Wrapper>
  );
}
